import type { LangMap } from "./index";

const en = {
  title: "Get me ready for class",
  subtitle: "A short preview so tomorrow's lesson feels familiar",
  disclaimerTitle: "These are estimates, not certainties",
  disclaimer:
    "Based on your program and the lessons already done, this is the most probable topic. Your teacher may change the order, skip something or do a review.",
  noTimetable:
    "Add your class timetable in the Planner (you can take a photo of it) so Lumen knows which lessons you have.",
  openPlanner: "Open the Planner",
  refresh: "Update the forecast",
  predicting: "Reading your program…",
  nextDay: "Next school day",
  lastLesson: "Last lesson",
  probableTopic: "Probable next topic",
  alsoPossible: "Could also be",
  why: "Why this guess",
  prepareIn: "Get ready in {{min}} min",
  confidence: {
    very_likely: "Very likely",
    likely: "Likely",
    possible: "Possible",
  },
  phrasing: "You will probably cover…",
  session: {
    title: "Get me ready for tomorrow",
    subtitle: "One single session across all your subjects",
    total: "Total time",
    build: "Build my session",
    minutes: "{{n}} min",
    custom: "Custom",
    auto: "Suggested",
    start: "Start",
    empty: "No lessons found for that day.",
  },
  prep: {
    title: "Before the lesson",
    duration: "How long do you have?",
    generate: "Prepare me",
    generating: "Preparing your preview…",
    first: "First of all",
    three: "3 things to know before the lesson",
    keywords: "Words you will probably hear",
    chain: "Look at this connection",
    question: "Guiding question",
    questionHint: "Keep this in mind during the lesson.",
    quiz: "Warm-up: 3 quick questions",
    quizHint: "This is not a test — it just wakes up what you already know.",
    correct: "Right",
    wrong: "Not quite",
    tipTitle: "Tip",
    tipFallback:
      "Don't try to memorise everything now. The goal is to recognise these concepts when the teacher explains them.",
    explain: "Explain it to me first",
    explaining: "Writing a simple preview…",
    explainHint: "3-5 minutes read",
    regenerate: "Regenerate",
    inProgram: "See it in your Program",
    done: "Preparation done",
  },
  log: {
    title: "What did you actually do in class?",
    subtitle: "Recording it makes the next forecasts better.",
    subject: "Subject",
    topic: "Topic covered",
    date: "Date",
    save: "Save lesson",
    saved: "Lesson saved",
    predicted: "We had predicted",
    result: "Result",
    accuracy: { correct: "Forecast correct", partial: "Partly correct", wrong: "Different topic" },
    stats: "{{correct}} correct forecasts out of {{total}}",
  },
  notifications: {
    title: "Pre-lesson reminders",
    body: "Get a nudge the evening before with how long it takes to be ready.",
  },
  widget: {
    title: "Tomorrow at school",
    next: "Next lesson",
    prepIn: "{{min}} min to get ready",
    cta: "Get me ready for tomorrow",
    start: "Start",
  },
};

const it: typeof en = {
  title: "Preparami per le prossime lezioni",
  subtitle: "Una preview breve per arrivare a lezione già orientato",
  disclaimerTitle: "Sono stime, non certezze",
  disclaimer:
    "In base al programma e alle lezioni svolte, questo è l'argomento più probabile. Il prof potrebbe cambiare ordine, saltare qualcosa o fare ripasso.",
  noTimetable:
    "Aggiungi l'orario scolastico nel Planner (puoi anche fotografarlo) così Lumen sa quali lezioni hai.",
  openPlanner: "Apri il Planner",
  refresh: "Aggiorna la previsione",
  predicting: "Sto leggendo il tuo programma…",
  nextDay: "Prossimo giorno di scuola",
  lastLesson: "Ultima lezione",
  probableTopic: "Prossimo argomento probabile",
  alsoPossible: "Potrebbe anche essere",
  why: "Perché questa previsione",
  prepareIn: "Preparati in {{min}} min",
  confidence: {
    very_likely: "Molto probabile",
    likely: "Probabile",
    possible: "Possibile",
  },
  phrasing: "Probabilmente affronterete…",
  session: {
    title: "Preparami per domani",
    subtitle: "Una sessione unica per tutte le materie",
    total: "Tempo totale",
    build: "Crea la sessione",
    minutes: "{{n}} min",
    custom: "Personalizzato",
    auto: "Consigliato",
    start: "Inizia",
    empty: "Nessuna lezione trovata per quel giorno.",
  },
  prep: {
    title: "Prima della lezione",
    duration: "Quanto tempo hai?",
    generate: "Preparati",
    generating: "Sto preparando la tua preview…",
    first: "Prima di tutto",
    three: "Le 3 cose da sapere prima della lezione",
    keywords: "Parole che probabilmente sentirai",
    chain: "Guarda questo collegamento",
    question: "Domanda guida",
    questionHint: "Tienila a mente durante la lezione.",
    quiz: "Riscaldamento: 3 domande veloci",
    quizHint: "Non è una verifica: serve solo ad attivare quello che già sai.",
    correct: "Giusto",
    wrong: "Non proprio",
    tipTitle: "Consiglio",
    tipFallback:
      "Non cercare di memorizzare tutto adesso. L'obiettivo è riconoscere questi concetti quando li sentirai spiegare dal professore.",
    explain: "Spiegamelo prima",
    explaining: "Sto scrivendo una spiegazione semplice…",
    explainHint: "3-5 minuti di lettura",
    regenerate: "Rigenera",
    inProgram: "Vedilo nel Programma",
    done: "Preparazione completata",
  },
  log: {
    title: "Cosa avete fatto davvero in classe?",
    subtitle: "Registrarlo rende migliori le prossime previsioni.",
    subject: "Materia",
    topic: "Argomento svolto",
    date: "Data",
    save: "Salva lezione",
    saved: "Lezione salvata",
    predicted: "Avevamo previsto",
    result: "Risultato",
    accuracy: { correct: "Previsione corretta", partial: "Parzialmente corretta", wrong: "Argomento diverso" },
    stats: "{{correct}} previsioni corrette su {{total}}",
  },
  notifications: {
    title: "Promemoria prima della lezione",
    body: "Ricevi un avviso la sera prima con quanto tempo serve per arrivare preparato.",
  },
  widget: {
    title: "Domani a scuola",
    next: "Prossima lezione",
    prepIn: "{{min}} min per prepararti",
    cta: "Preparami per domani",
    start: "Inizia",
  },
};

const es: typeof en = {
  title: "Prepárame para la clase",
  subtitle: "Un breve avance para que la lección de mañana te resulte familiar",
  disclaimerTitle: "Son estimaciones, no certezas",
  disclaimer:
    "Según tu programa y las lecciones ya hechas, este es el tema más probable. Tu profesor puede cambiar el orden, saltarse algo o hacer un repaso.",
  noTimetable:
    "Añade tu horario de clases en el Planner (puedes fotografiarlo) para que Lumen sepa qué lecciones tienes.",
  openPlanner: "Abrir el Planner",
  refresh: "Actualizar la previsión",
  predicting: "Leyendo tu programa…",
  nextDay: "Próximo día de clase",
  lastLesson: "Última lección",
  probableTopic: "Próximo tema probable",
  alsoPossible: "También podría ser",
  why: "Por qué esta previsión",
  prepareIn: "Prepárate en {{min}} min",
  confidence: {
    very_likely: "Muy probable",
    likely: "Probable",
    possible: "Posible",
  },
  phrasing: "Probablemente veréis…",
  session: {
    title: "Prepárame para mañana",
    subtitle: "Una sola sesión para todas tus asignaturas",
    total: "Tiempo total",
    build: "Crear mi sesión",
    minutes: "{{n}} min",
    custom: "Personalizado",
    auto: "Sugerido",
    start: "Empezar",
    empty: "No se han encontrado lecciones para ese día.",
  },
  prep: {
    title: "Antes de la lección",
    duration: "¿Cuánto tiempo tienes?",
    generate: "Prepárame",
    generating: "Preparando tu avance…",
    first: "Antes que nada",
    three: "3 cosas que saber antes de la lección",
    keywords: "Palabras que probablemente escucharás",
    chain: "Fíjate en esta conexión",
    question: "Pregunta guía",
    questionHint: "Ten esto en mente durante la lección.",
    quiz: "Calentamiento: 3 preguntas rápidas",
    quizHint: "No es un examen, solo despierta lo que ya sabes.",
    correct: "Correcto",
    wrong: "No del todo",
    tipTitle: "Consejo",
    tipFallback:
      "No intentes memorizarlo todo ahora. El objetivo es reconocer estos conceptos cuando el profesor los explique.",
    explain: "Explícamelo antes",
    explaining: "Escribiendo un resumen sencillo…",
    explainHint: "3-5 minutos de lectura",
    regenerate: "Regenerar",
    inProgram: "Verlo en tu Programa",
    done: "Preparación completada",
  },
  log: {
    title: "¿Qué hicisteis realmente en clase?",
    subtitle: "Registrarlo mejora las próximas previsiones.",
    subject: "Asignatura",
    topic: "Tema tratado",
    date: "Fecha",
    save: "Guardar lección",
    saved: "Lección guardada",
    predicted: "Habíamos previsto",
    result: "Resultado",
    accuracy: { correct: "Previsión correcta", partial: "Parcialmente correcta", wrong: "Tema diferente" },
    stats: "{{correct}} previsiones correctas de {{total}}",
  },
  notifications: {
    title: "Recordatorios antes de la lección",
    body: "Recibe un aviso la noche anterior con cuánto tiempo necesitas para estar preparado.",
  },
  widget: {
    title: "Mañana en clase",
    next: "Próxima lección",
    prepIn: "{{min}} min para prepararte",
    cta: "Prepárame para mañana",
    start: "Empezar",
  },
};

const fr: typeof en = {
  title: "Prépare-moi pour le cours",
  subtitle: "Un bref aperçu pour que la leçon de demain te soit familière",
  disclaimerTitle: "Ce sont des estimations, pas des certitudes",
  disclaimer:
    "D'après ton programme et les leçons déjà faites, voici le sujet le plus probable. Ton professeur peut changer l'ordre, sauter quelque chose ou faire une révision.",
  noTimetable:
    "Ajoute ton emploi du temps dans le Planner (tu peux le prendre en photo) pour que Lumen sache quels cours tu as.",
  openPlanner: "Ouvrir le Planner",
  refresh: "Mettre à jour la prévision",
  predicting: "Lecture de ton programme…",
  nextDay: "Prochain jour d'école",
  lastLesson: "Dernier cours",
  probableTopic: "Prochain sujet probable",
  alsoPossible: "Pourrait aussi être",
  why: "Pourquoi cette hypothèse",
  prepareIn: "Prépare-toi en {{min}} min",
  confidence: {
    very_likely: "Très probable",
    likely: "Probable",
    possible: "Possible",
  },
  phrasing: "Vous aborderez probablement…",
  session: {
    title: "Prépare-moi pour demain",
    subtitle: "Une seule séance pour toutes tes matières",
    total: "Temps total",
    build: "Créer ma séance",
    minutes: "{{n}} min",
    custom: "Personnalisé",
    auto: "Suggéré",
    start: "Commencer",
    empty: "Aucun cours trouvé pour ce jour.",
  },
  prep: {
    title: "Avant le cours",
    duration: "Combien de temps as-tu ?",
    generate: "Prépare-moi",
    generating: "Préparation de ton aperçu…",
    first: "Avant tout",
    three: "3 choses à savoir avant le cours",
    keywords: "Mots que tu entendras probablement",
    chain: "Regarde ce lien",
    question: "Question guide",
    questionHint: "Garde-la en tête pendant le cours.",
    quiz: "Échauffement : 3 questions rapides",
    quizHint: "Ce n'est pas un contrôle, ça réveille juste ce que tu sais déjà.",
    correct: "Juste",
    wrong: "Pas tout à fait",
    tipTitle: "Astuce",
    tipFallback:
      "N'essaie pas de tout mémoriser maintenant. L'objectif est de reconnaître ces notions quand le professeur les expliquera.",
    explain: "Explique-le-moi d'abord",
    explaining: "Rédaction d'un aperçu simple…",
    explainHint: "3-5 minutes de lecture",
    regenerate: "Régénérer",
    inProgram: "Voir dans ton Programme",
    done: "Préparation terminée",
  },
  log: {
    title: "Qu'avez-vous vraiment fait en classe ?",
    subtitle: "L'enregistrer améliore les prochaines prévisions.",
    subject: "Matière",
    topic: "Sujet traité",
    date: "Date",
    save: "Enregistrer le cours",
    saved: "Cours enregistré",
    predicted: "Nous avions prévu",
    result: "Résultat",
    accuracy: { correct: "Prévision correcte", partial: "Partiellement correcte", wrong: "Sujet différent" },
    stats: "{{correct}} prévisions correctes sur {{total}}",
  },
  notifications: {
    title: "Rappels avant le cours",
    body: "Reçois un rappel la veille au soir avec le temps nécessaire pour être prêt.",
  },
  widget: {
    title: "Demain à l'école",
    next: "Prochain cours",
    prepIn: "{{min}} min pour te préparer",
    cta: "Prépare-moi pour demain",
    start: "Commencer",
  },
};

const de: typeof en = {
  title: "Bereite mich auf den Unterricht vor",
  subtitle: "Eine kurze Vorschau, damit dir die morgige Stunde vertraut vorkommt",
  disclaimerTitle: "Das sind Schätzungen, keine Gewissheiten",
  disclaimer:
    "Basierend auf deinem Lehrplan und den bereits behandelten Stunden ist dies das wahrscheinlichste Thema. Dein Lehrer kann die Reihenfolge ändern, etwas überspringen oder wiederholen.",
  noTimetable:
    "Füge deinen Stundenplan im Planner hinzu (du kannst ihn auch fotografieren), damit Lumen weiß, welche Stunden du hast.",
  openPlanner: "Planner öffnen",
  refresh: "Prognose aktualisieren",
  predicting: "Lese deinen Lehrplan…",
  nextDay: "Nächster Schultag",
  lastLesson: "Letzte Stunde",
  probableTopic: "Wahrscheinliches nächstes Thema",
  alsoPossible: "Könnte auch sein",
  why: "Warum diese Vermutung",
  prepareIn: "In {{min}} Min. bereit sein",
  confidence: {
    very_likely: "Sehr wahrscheinlich",
    likely: "Wahrscheinlich",
    possible: "Möglich",
  },
  phrasing: "Ihr werdet wahrscheinlich behandeln…",
  session: {
    title: "Bereite mich auf morgen vor",
    subtitle: "Eine einzige Sitzung für alle deine Fächer",
    total: "Gesamtzeit",
    build: "Meine Sitzung erstellen",
    minutes: "{{n}} Min",
    custom: "Benutzerdefiniert",
    auto: "Vorgeschlagen",
    start: "Starten",
    empty: "Keine Stunden für diesen Tag gefunden.",
  },
  prep: {
    title: "Vor der Stunde",
    duration: "Wie viel Zeit hast du?",
    generate: "Bereite mich vor",
    generating: "Deine Vorschau wird vorbereitet…",
    first: "Zuallererst",
    three: "3 Dinge, die du vor der Stunde wissen solltest",
    keywords: "Wörter, die du wahrscheinlich hören wirst",
    chain: "Schau dir diese Verbindung an",
    question: "Leitfrage",
    questionHint: "Behalte das während der Stunde im Kopf.",
    quiz: "Aufwärmen: 3 kurze Fragen",
    quizHint: "Das ist kein Test — es weckt nur, was du schon weißt.",
    correct: "Richtig",
    wrong: "Nicht ganz",
    tipTitle: "Tipp",
    tipFallback:
      "Versuche jetzt nicht, alles auswendig zu lernen. Ziel ist es, diese Konzepte wiederzuerkennen, wenn der Lehrer sie erklärt.",
    explain: "Erklär es mir zuerst",
    explaining: "Eine einfache Vorschau wird geschrieben…",
    explainHint: "3-5 Minuten Lesezeit",
    regenerate: "Neu generieren",
    inProgram: "In deinem Programm ansehen",
    done: "Vorbereitung abgeschlossen",
  },
  log: {
    title: "Was habt ihr wirklich im Unterricht gemacht?",
    subtitle: "Es festzuhalten verbessert die nächsten Prognosen.",
    subject: "Fach",
    topic: "Behandeltes Thema",
    date: "Datum",
    save: "Stunde speichern",
    saved: "Stunde gespeichert",
    predicted: "Wir hatten vorhergesagt",
    result: "Ergebnis",
    accuracy: { correct: "Prognose richtig", partial: "Teilweise richtig", wrong: "Anderes Thema" },
    stats: "{{correct}} richtige Prognosen von {{total}}",
  },
  notifications: {
    title: "Erinnerungen vor der Stunde",
    body: "Erhalte am Vorabend einen Hinweis, wie lange die Vorbereitung dauert.",
  },
  widget: {
    title: "Morgen in der Schule",
    next: "Nächste Stunde",
    prepIn: "{{min}} Min. zur Vorbereitung",
    cta: "Bereite mich auf morgen vor",
    start: "Starten",
  },
};

const pt: typeof en = {
  title: "Prepara-me para a aula",
  subtitle: "Uma pré-visualização breve para que a aula de amanhã pareça familiar",
  disclaimerTitle: "São estimativas, não certezas",
  disclaimer:
    "Com base no teu programa e nas aulas já dadas, este é o tema mais provável. O professor pode mudar a ordem, saltar algo ou fazer uma revisão.",
  noTimetable:
    "Adiciona o teu horário de aulas no Planner (podes fotografá-lo) para que o Lumen saiba quais aulas tens.",
  openPlanner: "Abrir o Planner",
  refresh: "Atualizar a previsão",
  predicting: "A ler o teu programa…",
  nextDay: "Próximo dia de aulas",
  lastLesson: "Última aula",
  probableTopic: "Próximo tema provável",
  alsoPossible: "Também pode ser",
  why: "Porquê esta previsão",
  prepareIn: "Prepara-te em {{min}} min",
  confidence: {
    very_likely: "Muito provável",
    likely: "Provável",
    possible: "Possível",
  },
  phrasing: "Vão provavelmente abordar…",
  session: {
    title: "Prepara-me para amanhã",
    subtitle: "Uma única sessão para todas as tuas disciplinas",
    total: "Tempo total",
    build: "Criar a minha sessão",
    minutes: "{{n}} min",
    custom: "Personalizado",
    auto: "Sugerido",
    start: "Começar",
    empty: "Não foram encontradas aulas para esse dia.",
  },
  prep: {
    title: "Antes da aula",
    duration: "Quanto tempo tens?",
    generate: "Prepara-me",
    generating: "A preparar a tua pré-visualização…",
    first: "Antes de mais",
    three: "3 coisas a saber antes da aula",
    keywords: "Palavras que provavelmente vais ouvir",
    chain: "Repara nesta ligação",
    question: "Pergunta orientadora",
    questionHint: "Tem isto em mente durante a aula.",
    quiz: "Aquecimento: 3 perguntas rápidas",
    quizHint: "Não é um teste — serve só para ativar o que já sabes.",
    correct: "Certo",
    wrong: "Não é bem isso",
    tipTitle: "Dica",
    tipFallback:
      "Não tentes memorizar tudo agora. O objetivo é reconhecer estes conceitos quando o professor os explicar.",
    explain: "Explica-me primeiro",
    explaining: "A escrever uma pré-visualização simples…",
    explainHint: "3-5 minutos de leitura",
    regenerate: "Regenerar",
    inProgram: "Ver no teu Programa",
    done: "Preparação concluída",
  },
  log: {
    title: "O que fizeram realmente na aula?",
    subtitle: "Registá-lo melhora as próximas previsões.",
    subject: "Disciplina",
    topic: "Tema abordado",
    date: "Data",
    save: "Guardar aula",
    saved: "Aula guardada",
    predicted: "Tínhamos previsto",
    result: "Resultado",
    accuracy: { correct: "Previsão correta", partial: "Parcialmente correta", wrong: "Tema diferente" },
    stats: "{{correct}} previsões corretas de {{total}}",
  },
  notifications: {
    title: "Lembretes antes da aula",
    body: "Recebe um aviso na noite anterior com quanto tempo precisas para estar preparado.",
  },
  widget: {
    title: "Amanhã na escola",
    next: "Próxima aula",
    prepIn: "{{min}} min para te preparares",
    cta: "Prepara-me para amanhã",
    start: "Começar",
  },
};

export const prep: LangMap = { en, it, es, fr, de, pt };
